import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

export interface SortableCard {
  id: string;
  /** Short name for the drag handle's tooltip and screen readers. */
  label: string;
  node: React.ReactNode;
}

interface SortableCardListProps {
  /** Cards in the order to show them. */
  cards: SortableCard[];
  /** Called with the new id order after a card is dropped somewhere else. */
  onReorder: (ids: string[]) => void;
  className?: string;
  /** `touch`: a wider, shaded grip that's easy to grab with a finger (phone reorder mode). */
  handleSize?: 'default' | 'touch';
}

/**
 * Vertical list of boxes that can be dragged up or down by the grip on their left.
 * The whole box moves together; the grip is the only drag start so inputs keep working.
 */
export function SortableCardList({ cards, onReorder, className, handleSize = 'default' }: SortableCardListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const ids = cards.map((card) => card.id);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove(ids, oldIndex, newIndex));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className={className}>
          {cards.map((card) => (
            <SortableCardItem key={card.id} card={card} handleSize={handleSize} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SortableCardItem({ card, handleSize }: { card: SortableCard; handleSize: 'default' | 'touch' }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id: card.id });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={`group/sortable flex items-stretch gap-1 ${isDragging ? 'relative opacity-90 drop-shadow-lg' : ''}`}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        className={`flex shrink-0 cursor-grab touch-none justify-center rounded ${handleSize === 'touch' ? 'w-7 items-start pt-3 text-gray-500 bg-gray-100' : 'w-4 items-start pt-3 text-gray-300'} transition-colors hover:bg-gray-100 hover:text-gray-500 active:cursor-grabbing group-hover/sortable:text-gray-400 ${isDragging ? 'text-primary-500' : ''}`}
        title={`Drag to move ${card.label}`}
        aria-label={`Move ${card.label}`}
      >
        <GripVertical className={handleSize === 'touch' ? 'h-5 w-5' : 'h-4 w-4'} />
      </button>
      <div className={`min-w-0 flex-1 ${isDragging ? 'rounded-lg ring-2 ring-primary-300' : ''}`}>
        {card.node}
      </div>
    </div>
  );
}
